import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  FileText, Settings as SettingsIcon, Users, CreditCard, LogOut, Plus,
  Pencil, Trash2, Upload, Loader2, Eye, EyeOff, Lock, Rocket, Check,
} from "lucide-react";
import { api, apiError, BACKEND } from "@/lib/api";

const EMPTY_PAPER = {
  title: "", abstract: "", content_md: "", category: "", tags: "",
  pdf_url: "", cover_url: "", supporters_only: false, published: true, featured: false,
};

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 block mb-1.5">{label}</span>
      {children}
    </label>
  );
}

const inputCls =
  "w-full bg-[#07090E] border border-[#1E2638] rounded-md px-3.5 py-2.5 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-sky-400/60 transition-colors";

function Toggle({ checked, onChange, label, testid }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      data-testid={testid}
      className={`flex items-center gap-2.5 font-mono text-[11px] uppercase tracking-[0.15em] rounded-full border px-4 py-2 transition-colors ${
        checked ? "border-sky-400/60 bg-sky-400/10 text-sky-300" : "border-[#1E2638] text-slate-500"
      }`}
    >
      {checked ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
      {label}: {checked ? "On" : "Off"}
    </button>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null); // null = checking, false = logged out
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);
  const [tab, setTab] = useState("papers");

  const [papers, setPapers] = useState([]);
  const [editing, setEditing] = useState(null); // null = closed, object = form
  const [savingPaper, setSavingPaper] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [subscribers, setSubscribers] = useState([]);
  const [donations, setDonations] = useState([]);
  const [rollout, setRollout] = useState([]);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  const loadAll = useCallback(() => {
    api.get("/admin/papers").then((r) => setPapers(r.data)).catch(() => {});
    api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/admin/subscribers").then((r) => setSubscribers(r.data)).catch(() => {});
    api.get("/admin/donations").then((r) => setDonations(r.data)).catch(() => {});
    api.get("/admin/rollout").then((r) => setRollout(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) loadAll();
  }, [user, loadAll]);

  const toggleRollout = async (i) => {
    const next = rollout.map((item, idx) => (idx === i ? { ...item, done: !item.done } : item));
    setRollout(next);
    try {
      await api.put("/admin/rollout", { items: next });
    } catch (err) {
      toast.error(apiError(err, "Could not save checklist"));
    }
  };

  const login = async (e) => {
    e.preventDefault();
    setLoginBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      toast.success(`Welcome back, ${data.name}`);
    } catch (err) {
      toast.error(apiError(err, "Login failed"));
    } finally {
      setLoginBusy(false);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
    toast.success("Logged out");
  };

  const savePaper = async (e) => {
    e.preventDefault();
    setSavingPaper(true);
    const payload = {
      ...editing,
      tags: typeof editing.tags === "string" ? editing.tags.split(",").map((t) => t.trim()).filter(Boolean) : editing.tags,
    };
    try {
      if (editing.id) await api.put(`/admin/papers/${editing.id}`, payload);
      else await api.post("/admin/papers", payload);
      toast.success(editing.id ? "Paper updated" : "Paper published");
      setEditing(null);
      loadAll();
    } catch (err) {
      toast.error(apiError(err, "Save failed"));
    } finally {
      setSavingPaper(false);
    }
  };

  const deletePaper = async (p) => {
    if (!window.confirm(`Delete "${p.title}" permanently?`)) return;
    try {
      await api.delete(`/admin/papers/${p.id}`);
      toast.success("Paper deleted");
      loadAll();
    } catch (err) {
      toast.error(apiError(err, "Delete failed"));
    }
  };

  const uploadPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/upload", form);
      setEditing((ed) => ({ ...ed, pdf_url: data.url }));
      toast.success("PDF attached");
    } catch (err) {
      toast.error(apiError(err, "Upload failed"));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      await api.put("/admin/settings", { ...settings, donation_goal: Number(settings.donation_goal) || 0 });
      toast.success("Settings saved");
    } catch (err) {
      toast.error(apiError(err, "Save failed"));
    } finally {
      setSavingSettings(false);
    }
  };

  /* ---------- LOGIN ---------- */
  if (user === null) {
    return <main className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 text-sky-400 animate-spin" /></main>;
  }

  if (user === false) {
    return (
      <main className="min-h-screen flex items-center justify-center relative hero-glow noise-overlay">
        <div className="relative w-full max-w-md mx-4 rounded-2xl border border-[#1E2638] bg-[#131824] p-10">
          <div className="flex items-center gap-3 mb-8">
            <span className="w-10 h-10 rounded-lg bg-sky-400/10 border border-sky-400/40 flex items-center justify-center">
              <Lock className="w-5 h-5 text-sky-400" />
            </span>
            <div>
              <h1 className="font-display font-bold text-xl">Admin Console</h1>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">SoverTech Research Fund</p>
            </div>
          </div>
          <form onSubmit={login} className="space-y-4">
            <Field label="Email">
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email" className={inputCls} placeholder="admin@sovertech.org" required />
            </Field>
            <Field label="Password">
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password" className={inputCls} placeholder="••••••••" required />
            </Field>
            <button type="submit" disabled={loginBusy} data-testid="admin-login-submit" className="w-full bg-sky-400 text-[#07090E] font-semibold text-sm rounded-md py-3 hover:bg-sky-300 transition-colors disabled:opacity-50">
              {loginBusy ? "Signing in…" : "Sign in"}
            </button>
          </form>
          <div className="mt-6 rounded-lg border border-[#1E2638] bg-[#07090E] p-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">Demo credentials</p>
            <p className="font-mono text-xs text-slate-400">admin@sovertech.org / admin123</p>
          </div>
        </div>
      </main>
    );
  }

  /* ---------- DASHBOARD ---------- */
  const TABS = [
    { id: "papers", label: "Papers", icon: FileText },
    { id: "settings", label: "Settings", icon: SettingsIcon },
    { id: "subscribers", label: `Subscribers (${subscribers.length})`, icon: Users },
    { id: "donations", label: `Donations (${donations.length})`, icon: CreditCard },
    { id: "rollout", label: "Rollout", icon: Rocket },
  ];

  return (
    <main className="min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-sky-400 mb-2">Console</p>
            <h1 className="font-display font-extrabold tracking-tight text-3xl">Publisher Dashboard</h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="font-mono text-xs text-slate-500">{user.email}</span>
            <button onClick={logout} data-testid="admin-logout-btn" className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-slate-400 hover:text-rose-400 transition-colors">
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-10 border-b border-[#1E2638] pb-4">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] rounded-full px-5 py-2.5 border transition-colors ${
                tab === t.id ? "bg-sky-400 text-[#07090E] border-sky-400 font-semibold" : "border-[#1E2638] text-slate-400 hover:border-sky-400/50 hover:text-sky-300"
              }`}
            >
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>

        {/* ---------- PAPERS TAB ---------- */}
        {tab === "papers" && !editing && (
          <div>
            <button
              onClick={() => setEditing({ ...EMPTY_PAPER })}
              data-testid="admin-create-paper-btn"
              className="mb-8 flex items-center gap-2 bg-sky-400 text-[#07090E] font-semibold text-sm rounded-full px-6 py-3 hover:bg-sky-300 transition-colors"
            >
              <Plus className="w-4 h-4" /> New paper
            </button>
            <div className="space-y-3">
              {papers.map((p) => (
                <div key={p.id} data-testid={`admin-paper-row-${p.slug}`} className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-[#1E2638] bg-[#131824] px-5 py-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2.5 mb-1">
                      <h3 className="font-display font-semibold text-base truncate">{p.title}</h3>
                      {p.supporters_only && <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-amber-400 border border-amber-400/40 rounded-full px-2 py-0.5">Supporters</span>}
                      {!p.published && <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-slate-500 border border-[#1E2638] rounded-full px-2 py-0.5">Draft</span>}
                    </div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">
                      {p.category} · {p.views || 0} reads · /paper/{p.slug}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setEditing({ ...p, tags: (p.tags || []).join(", "), pdf_url: p.pdf_url || "", cover_url: p.cover_url || "" })}
                      data-testid={`admin-edit-${p.slug}`}
                      className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.1em] border border-[#1E2638] rounded-md px-3.5 py-2 text-slate-300 hover:border-sky-400/50 hover:text-sky-300 transition-colors"
                    >
                      <Pencil className="w-3 h-3" /> Edit
                    </button>
                    <button
                      onClick={() => deletePaper(p)}
                      data-testid={`admin-delete-${p.slug}`}
                      className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.1em] border border-[#1E2638] rounded-md px-3.5 py-2 text-slate-300 hover:border-rose-400/50 hover:text-rose-400 transition-colors"
                    >
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "papers" && editing && (
          <form onSubmit={savePaper} className="max-w-3xl space-y-5" data-testid="admin-paper-form">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-bold text-xl">{editing.id ? "Edit paper" : "New paper"}</h2>
              <button type="button" onClick={() => setEditing(null)} data-testid="admin-paper-cancel-btn" className="font-mono text-xs uppercase tracking-[0.15em] text-slate-500 hover:text-slate-300">
                Cancel
              </button>
            </div>
            <Field label="Title">
              <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="admin-paper-title-input" className={inputCls} required />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Category">
                <input value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="admin-paper-category-input" className={inputCls} placeholder="Quantum Computing" required />
              </Field>
              <Field label="Tags (comma separated)">
                <input value={editing.tags} onChange={(e) => setEditing({ ...editing, tags: e.target.value })} data-testid="admin-paper-tags-input" className={inputCls} placeholder="quantum, mesh" />
              </Field>
            </div>
            <Field label="Abstract">
              <textarea value={editing.abstract} onChange={(e) => setEditing({ ...editing, abstract: e.target.value })} data-testid="admin-paper-abstract-input" className={`${inputCls} min-h-[90px]`} required />
            </Field>
            <Field label="Content (Markdown — use ## for sections)">
              <textarea value={editing.content_md} onChange={(e) => setEditing({ ...editing, content_md: e.target.value })} data-testid="admin-paper-markdown-content" className={`${inputCls} min-h-[320px] font-mono text-xs leading-relaxed`} required />
            </Field>
            <Field label="Cover image URL (optional)">
              <input value={editing.cover_url} onChange={(e) => setEditing({ ...editing, cover_url: e.target.value })} data-testid="admin-paper-cover-input" className={inputCls} placeholder="https://…" />
            </Field>
            <Field label="PDF attachment">
              <div className="flex items-center gap-3">
                <input value={editing.pdf_url} onChange={(e) => setEditing({ ...editing, pdf_url: e.target.value })} data-testid="admin-paper-pdf-input" className={inputCls} placeholder="/api/uploads/… or external URL" />
                <input ref={fileRef} type="file" accept="application/pdf" onChange={uploadPdf} className="hidden" data-testid="admin-pdf-file-input" />
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="admin-pdf-upload-btn" className="shrink-0 flex items-center gap-2 border border-[#1E2638] rounded-md px-4 py-2.5 text-sm text-slate-300 hover:border-sky-400/50 hover:text-sky-300 transition-colors disabled:opacity-50">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload
                </button>
              </div>
              {editing.pdf_url && (
                <a href={editing.pdf_url.startsWith("http") ? editing.pdf_url : `${BACKEND}${editing.pdf_url}`} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-xs text-sky-400 hover:underline">
                  Preview attached PDF
                </a>
              )}
            </Field>
            <div className="flex flex-wrap gap-3">
              <Toggle checked={editing.published} onChange={(v) => setEditing({ ...editing, published: v })} label="Published" testid="admin-paper-published-toggle" />
              <Toggle checked={editing.supporters_only} onChange={(v) => setEditing({ ...editing, supporters_only: v })} label="Supporters only" testid="admin-paper-paywall-toggle" />
              <Toggle checked={editing.featured} onChange={(v) => setEditing({ ...editing, featured: v })} label="Featured" testid="admin-paper-featured-toggle" />
            </div>
            <button type="submit" disabled={savingPaper} data-testid="admin-paper-save-btn" className="bg-sky-400 text-[#07090E] font-semibold text-sm rounded-full px-8 py-3.5 hover:bg-sky-300 transition-colors disabled:opacity-50">
              {savingPaper ? "Saving…" : editing.id ? "Save changes" : "Publish paper"}
            </button>
          </form>
        )}

        {/* ---------- SETTINGS TAB ---------- */}
        {tab === "settings" && settings && (
          <form onSubmit={saveSettings} className="max-w-3xl space-y-5" data-testid="admin-settings-form">
            <h2 className="font-display font-bold text-xl">Site settings</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Donation goal (USD)">
                <input type="number" value={settings.donation_goal} onChange={(e) => setSettings({ ...settings, donation_goal: e.target.value })} data-testid="admin-settings-goal-input" className={inputCls} />
              </Field>
              <Field label="Funds raised so far (USD, manual)">
                <input type="number" value={settings.raised_offline ?? 0} onChange={(e) => setSettings({ ...settings, raised_offline: e.target.value })} data-testid="admin-settings-raised-input" className={inputCls} />
              </Field>
              <Field label="Supporter access code">
                <input value={settings.supporter_code} onChange={(e) => setSettings({ ...settings, supporter_code: e.target.value })} data-testid="admin-settings-code-input" className={`${inputCls} font-mono tracking-widest text-amber-300`} />
              </Field>
            </div>
            <Field label="PayPal donation URL">
              <input value={settings.paypal_url} onChange={(e) => setSettings({ ...settings, paypal_url: e.target.value })} data-testid="admin-settings-paypal-input" className={inputCls} />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Bitcoin address">
                <input value={settings.btc} onChange={(e) => setSettings({ ...settings, btc: e.target.value })} data-testid="admin-settings-btc-input" className={`${inputCls} font-mono text-xs`} />
              </Field>
              <Field label="Ethereum address">
                <input value={settings.eth} onChange={(e) => setSettings({ ...settings, eth: e.target.value })} data-testid="admin-settings-eth-input" className={`${inputCls} font-mono text-xs`} />
              </Field>
              <Field label="Solana address">
                <input value={settings.sol} onChange={(e) => setSettings({ ...settings, sol: e.target.value })} data-testid="admin-settings-sol-input" className={`${inputCls} font-mono text-xs`} />
              </Field>
              <Field label="USDT (TRC20) address">
                <input value={settings.usdt} onChange={(e) => setSettings({ ...settings, usdt: e.target.value })} data-testid="admin-settings-usdt-input" className={`${inputCls} font-mono text-xs`} />
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Bank name">
                <input value={settings.bank_name} onChange={(e) => setSettings({ ...settings, bank_name: e.target.value })} data-testid="admin-settings-bank-name-input" className={inputCls} />
              </Field>
              <Field label="Account name">
                <input value={settings.bank_account_name} onChange={(e) => setSettings({ ...settings, bank_account_name: e.target.value })} data-testid="admin-settings-bank-holder-input" className={inputCls} />
              </Field>
              <Field label="Account number">
                <input value={settings.bank_account_number} onChange={(e) => setSettings({ ...settings, bank_account_number: e.target.value })} data-testid="admin-settings-bank-account-input" className={inputCls} />
              </Field>
              <Field label="Routing / IBAN">
                <input value={settings.bank_routing} onChange={(e) => setSettings({ ...settings, bank_routing: e.target.value })} data-testid="admin-settings-bank-routing-input" className={inputCls} />
              </Field>
              <Field label="Transfer reference">
                <input value={settings.bank_reference} onChange={(e) => setSettings({ ...settings, bank_reference: e.target.value })} data-testid="admin-settings-bank-ref-input" className={inputCls} />
              </Field>
            </div>
            <button type="submit" disabled={savingSettings} data-testid="admin-settings-save-btn" className="bg-sky-400 text-[#07090E] font-semibold text-sm rounded-full px-8 py-3.5 hover:bg-sky-300 transition-colors disabled:opacity-50">
              {savingSettings ? "Saving…" : "Save settings"}
            </button>
          </form>
        )}

        {/* ---------- SUBSCRIBERS TAB ---------- */}
        {tab === "subscribers" && (
          <div className="max-w-2xl">
            {subscribers.length === 0 ? (
              <p className="text-slate-500 text-sm">No subscribers yet.</p>
            ) : (
              <div className="rounded-xl border border-[#1E2638] bg-[#131824] divide-y divide-[#1E2638]">
                {subscribers.map((s) => (
                  <div key={s.email} data-testid={`subscriber-row-${s.email}`} className="flex items-center justify-between px-5 py-3.5">
                    <span className="text-sm text-slate-200">{s.email}</span>
                    <span className="font-mono text-[10px] text-slate-500">{new Date(s.created_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ---------- ROLLOUT TAB ---------- */}
        {tab === "rollout" && (
          <div className="max-w-2xl" data-testid="rollout-checklist">
            <p className="text-sm text-slate-400 mb-6 leading-relaxed">
              Launch-day sequence. Check items off as they go live — saved automatically.
            </p>
            <div className="space-y-3">
              {rollout.map((item, i) => (
                <button
                  key={item.id}
                  onClick={() => toggleRollout(i)}
                  data-testid={`rollout-item-${item.id}`}
                  className={`w-full flex items-center gap-4 rounded-xl border px-5 py-4 text-left transition-colors ${
                    item.done
                      ? "border-emerald-400/40 bg-emerald-400/5"
                      : "border-[#1E2638] bg-[#131824] hover:border-sky-400/40"
                  }`}
                >
                  <span className={`w-6 h-6 rounded-md border flex items-center justify-center shrink-0 transition-colors ${
                    item.done ? "border-emerald-400 bg-emerald-400/20" : "border-[#1E2638]"
                  }`}>
                    {item.done && <Check className="w-4 h-4 text-emerald-400" />}
                  </span>
                  <span className={`text-sm ${item.done ? "text-slate-500 line-through" : "text-slate-200"}`}>
                    {item.label}
                  </span>
                  <span className="ml-auto font-mono text-[10px] text-slate-600">{String(i + 1).padStart(2, "0")}</span>
                </button>
              ))}
            </div>
            <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">
              {rollout.filter((r) => r.done).length} / {rollout.length} complete
            </p>
          </div>
        )}

        {/* ---------- DONATIONS TAB ---------- */}
        {tab === "donations" && (
          <div className="max-w-3xl">
            {donations.length === 0 ? (
              <p className="text-slate-500 text-sm">No donations yet.</p>
            ) : (
              <div className="rounded-xl border border-[#1E2638] bg-[#131824] divide-y divide-[#1E2638]">
                {donations.map((d) => (
                  <div key={d.session_id} data-testid={`donation-row-${d.session_id.slice(-6)}`} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5">
                    <div>
                      <p className="font-display font-bold text-base">
                        {d.amount ? `$${(d.amount / 100).toFixed(2)}` : "BTC pledge"}{" "}
                        <span className="font-mono text-[10px] uppercase text-slate-500">{d.currency}</span>
                      </p>
                      <p className="font-mono text-[10px] text-slate-500">{d.customer_email || d.session_id.slice(0, 20) + "…"}</p>
                    </div>
                    <span className={`font-mono text-[10px] uppercase tracking-[0.15em] rounded-full border px-3 py-1 ${
                      d.payment_status === "paid" ? "text-emerald-400 border-emerald-400/40" : d.payment_status === "pledged" ? "text-amber-400 border-amber-400/40" : "text-slate-500 border-[#1E2638]"
                    }`}>
                      {d.payment_status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
